import type {
  OperationCapabilities,
  OperationCapability,
} from '../model/capabilities';
import type { BlockedReason } from '../model/operations';
import { OPERATION_PRESENTATION } from './operationPresentation';
import type {
  RowActionId,
  RowMenuEntry,
  SectionActionId,
  SectionMenuEntry,
} from './rowTypes';
import type { LabelKey, Message } from './messages';

export type MenuPresentation<TActionId extends string = RowActionId> = {
  labelKeys: Record<TActionId, LabelKey>;
  blockedMessages: Record<BlockedReason, Message | null>;
};

function resolveActionId({
  canHide,
  canUnhide,
}: OperationCapabilities): RowActionId | null {
  if (canHide) {
    return 'hide';
  }

  return canUnhide ? 'unhide' : null;
}

export function createMenuEntries(
  capabilities: OperationCapabilities,
  { labelKeys, blockedMessages }: MenuPresentation
): RowMenuEntry[] {
  const id = resolveActionId(capabilities);

  if (!id) {
    return [];
  }

  const { blockedReason } = capabilities;

  return [
    {
      id,
      label: { key: labelKeys[id] },
      isDisabled: blockedReason !== null,
      disabledReason: blockedReason ? blockedMessages[blockedReason] : null,
    },
  ];
}

export function createSectionMenuEntries(
  capabilities: readonly OperationCapability[],
  { labelKeys, blockedMessages }: MenuPresentation<SectionActionId>
): SectionMenuEntry[] {
  return capabilities.map(({ kind, blockedReason }) => {
    const { action } = OPERATION_PRESENTATION[kind];

    return {
      id: action,
      label: { key: labelKeys[action] },
      isDisabled: blockedReason !== null,
      disabledReason: blockedReason ? blockedMessages[blockedReason] : null,
    };
  });
}
